import React, { useEffect, useRef, Component } from 'react';
// ── Types ──────────────────────────────────────────────────────────────────
interface Fiber {
  // Cubic bezier: P0 → CP1 → CP2 → P3
  p0x: number;
  p0y: number;
  cp1x: number;
  cp1y: number;
  cp2x: number;
  cp2y: number;
  p3x: number;
  p3y: number;
  // Animated offsets for organic breathing
  cp1ox: number;
  cp1oy: number;
  cp2ox: number;
  cp2oy: number;
  cp1vx: number;
  cp1vy: number;
  cp2vx: number;
  cp2vy: number;
  layer: number; // 0=far, 1=mid, 2=near
  hue: number;
  opacity: number;
  width: number;
  pulses: FiberPulse[];
  phase: number; // breathing phase offset
}
interface FiberPulse {
  t: number; // 0→1 along fiber
  speed: number;
  opacity: number;
  hue: number;
  size: number;
  trail: number; // trail length 0→1
}
// ── Helpers ────────────────────────────────────────────────────────────────
function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}
// Cubic bezier point
function cubicBezier(p0x: number, p0y: number, cp1x: number, cp1y: number, cp2x: number, cp2y: number, p3x: number, p3y: number, t: number) {
  const mt = 1 - t;
  const mt2 = mt * mt;
  const mt3 = mt2 * mt;
  const t2 = t * t;
  const t3 = t2 * t;
  return {
    x: mt3 * p0x + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * p3x,
    y: mt3 * p0y + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * p3y
  };
}
// Random point on a canvas edge
function randomEdgePoint(W: number, H: number): {
  x: number;
  y: number;
} {
  const side = Math.floor(Math.random() * 4);
  if (side === 0) return {
    x: Math.random() * W,
    y: -10
  };
  if (side === 1) return {
    x: W + 10,
    y: Math.random() * H
  };
  if (side === 2) return {
    x: Math.random() * W,
    y: H + 10
  };
  return {
    x: -10,
    y: Math.random() * H
  };
}
// Avoid center zone when placing control points
function avoidCenter(x: number, y: number, W: number, H: number, strength = 0.35): {
  x: number;
  y: number;
} {
  const cx = W / 2,
    cy = H / 2;
  const dx = x - cx,
    dy = y - cy;
  const d = Math.sqrt(dx * dx + dy * dy);
  const minDist = Math.min(W, H) * strength;
  if (d < minDist && d > 0) {
    const scale = minDist / d;
    return {
      x: cx + dx * scale,
      y: cy + dy * scale
    };
  }
  return {
    x,
    y
  };
}
// ── Fiber Builder ──────────────────────────────────────────────────────────
function buildFiber(W: number, H: number, layer: number): Fiber {
  const p0 = randomEdgePoint(W, H);
  const p3 = randomEdgePoint(W, H);
  // Control points: somewhere in the canvas, biased away from center
  let cp1 = {
    x: W * (0.15 + Math.random() * 0.7),
    y: H * (0.1 + Math.random() * 0.8)
  };
  let cp2 = {
    x: W * (0.15 + Math.random() * 0.7),
    y: H * (0.1 + Math.random() * 0.8)
  };
  cp1 = avoidCenter(cp1.x, cp1.y, W, H, 0.28);
  cp2 = avoidCenter(cp2.x, cp2.y, W, H, 0.28);
  // Layer properties
  const layerProps = [{
    opacityBase: 0.06,
    widthBase: 0.4,
    speedMult: 0.6,
    hueRange: [220, 260]
  }, {
    opacityBase: 0.12,
    widthBase: 0.7,
    speedMult: 1.0,
    hueRange: [200, 240]
  }, {
    opacityBase: 0.22,
    widthBase: 1.1,
    speedMult: 1.5,
    hueRange: [185, 220]
  } // near: bright, thick, fast, cyan-blue
  ];
  const lp = layerProps[layer];
  const hue = lp.hueRange[0] + Math.random() * (lp.hueRange[1] - lp.hueRange[0]);
  const fiber: Fiber = {
    p0x: p0.x,
    p0y: p0.y,
    cp1x: cp1.x,
    cp1y: cp1.y,
    cp2x: cp2.x,
    cp2y: cp2.y,
    p3x: p3.x,
    p3y: p3.y,
    cp1ox: 0,
    cp1oy: 0,
    cp2ox: 0,
    cp2oy: 0,
    cp1vx: (Math.random() - 0.5) * 0.015 * (layer + 1),
    cp1vy: (Math.random() - 0.5) * 0.015 * (layer + 1),
    cp2vx: (Math.random() - 0.5) * 0.012 * (layer + 1),
    cp2vy: (Math.random() - 0.5) * 0.012 * (layer + 1),
    layer,
    hue,
    opacity: lp.opacityBase + Math.random() * lp.opacityBase,
    width: lp.widthBase + Math.random() * lp.widthBase * 0.5,
    pulses: [],
    phase: Math.random() * Math.PI * 2
  };
  // Seed initial pulses
  const numPulses = Math.floor(Math.random() * 2) + (layer === 2 ? 2 : 1);
  for (let i = 0; i < numPulses; i++) {
    fiber.pulses.push({
      t: Math.random(),
      speed: (0.0003 + Math.random() * 0.0006) * lp.speedMult,
      opacity: 0.5 + Math.random() * 0.5,
      hue: hue + (Math.random() - 0.5) * 30,
      size: (1.5 + Math.random() * 2.5) * (layer + 1),
      trail: 0.06 + Math.random() * 0.08
    });
  }
  return fiber;
}
// ── Draw a fiber with glow ─────────────────────────────────────────────────
function drawFiber(ctx: CanvasRenderingContext2D, fiber: Fiber, timestamp: number, W: number, H: number) {
  const breathe = Math.sin(timestamp * 0.0004 + fiber.phase) * 0.15 + 0.85;
  const alpha = fiber.opacity * breathe;
  const cp1x = fiber.cp1x + fiber.cp1ox;
  const cp1y = fiber.cp1y + fiber.cp1oy;
  const cp2x = fiber.cp2x + fiber.cp2ox;
  const cp2y = fiber.cp2y + fiber.cp2oy;
  // Outer soft glow pass (wider, more transparent)
  ctx.beginPath();
  ctx.moveTo(fiber.p0x, fiber.p0y);
  ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, fiber.p3x, fiber.p3y);
  ctx.strokeStyle = `hsla(${fiber.hue},80%,60%,${alpha * 0.3})`;
  ctx.lineWidth = fiber.width * 3.5;
  ctx.lineCap = 'round';
  ctx.stroke();
  // Core fiber line
  ctx.beginPath();
  ctx.moveTo(fiber.p0x, fiber.p0y);
  ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, fiber.p3x, fiber.p3y);
  ctx.strokeStyle = `hsla(${fiber.hue},85%,65%,${alpha})`;
  ctx.lineWidth = fiber.width;
  ctx.stroke();
}
// ── Draw a pulse with trail ────────────────────────────────────────────────
function drawPulse(ctx: CanvasRenderingContext2D, fiber: Fiber, pulse: FiberPulse) {
  const cp1x = fiber.cp1x + fiber.cp1ox;
  const cp1y = fiber.cp1y + fiber.cp1oy;
  const cp2x = fiber.cp2x + fiber.cp2ox;
  const cp2y = fiber.cp2y + fiber.cp2oy;
  // Draw trail: series of fading dots behind the pulse head
  const trailSteps = 12;
  for (let i = trailSteps; i >= 0; i--) {
    const trailT = Math.max(0, pulse.t - i / trailSteps * pulse.trail);
    const pos = cubicBezier(fiber.p0x, fiber.p0y, cp1x, cp1y, cp2x, cp2y, fiber.p3x, fiber.p3y, trailT);
    const trailFade = 1 - i / trailSteps;
    const trailAlpha = pulse.opacity * trailFade * trailFade * 0.6;
    const trailSize = pulse.size * (0.3 + trailFade * 0.7);
    if (trailAlpha < 0.01) continue;
    // Hue shifts slightly along trail
    const trailHue = pulse.hue + (1 - trailFade) * 20;
    const g = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, trailSize * 2);
    g.addColorStop(0, `hsla(${trailHue},95%,75%,${trailAlpha})`);
    g.addColorStop(0.5, `hsla(${trailHue + 10},85%,65%,${trailAlpha * 0.4})`);
    g.addColorStop(1, `hsla(${trailHue},80%,60%,0)`);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, trailSize * 2, 0, Math.PI * 2);
    ctx.fill();
  }
  // Pulse head — bright core
  const headPos = cubicBezier(fiber.p0x, fiber.p0y, cp1x, cp1y, cp2x, cp2y, fiber.p3x, fiber.p3y, pulse.t);
  const headSize = pulse.size * 1.4;
  const hg = ctx.createRadialGradient(headPos.x, headPos.y, 0, headPos.x, headPos.y, headSize * 3);
  hg.addColorStop(0, `rgba(255,255,255,${pulse.opacity * 0.95})`);
  hg.addColorStop(0.2, `hsla(${pulse.hue},100%,80%,${pulse.opacity * 0.8})`);
  hg.addColorStop(0.6, `hsla(${pulse.hue + 15},90%,65%,${pulse.opacity * 0.3})`);
  hg.addColorStop(1, `hsla(${pulse.hue},80%,60%,0)`);
  ctx.fillStyle = hg;
  ctx.beginPath();
  ctx.arc(headPos.x, headPos.y, headSize * 3, 0, Math.PI * 2);
  ctx.fill();
}
// ── Simplified fiber draw for mobile (no double glow pass) ─────────────────
function drawFiberSimple(ctx: CanvasRenderingContext2D, fiber: Fiber, timestamp: number) {
  const breathe = Math.sin(timestamp * 0.0004 + fiber.phase) * 0.15 + 0.85;
  const alpha = fiber.opacity * breathe;
  const cp1x = fiber.cp1x + fiber.cp1ox;
  const cp1y = fiber.cp1y + fiber.cp1oy;
  const cp2x = fiber.cp2x + fiber.cp2ox;
  const cp2y = fiber.cp2y + fiber.cp2oy;
  ctx.beginPath();
  ctx.moveTo(fiber.p0x, fiber.p0y);
  ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, fiber.p3x, fiber.p3y);
  ctx.strokeStyle = `hsla(${fiber.hue},85%,65%,${alpha})`;
  ctx.lineWidth = fiber.width;
  ctx.lineCap = 'round';
  ctx.stroke();
}
// ── Simplified pulse draw for mobile (no expensive radial gradient trails) ─
function drawPulseSimple(ctx: CanvasRenderingContext2D, fiber: Fiber, pulse: FiberPulse) {
  const cp1x = fiber.cp1x + fiber.cp1ox;
  const cp1y = fiber.cp1y + fiber.cp1oy;
  const cp2x = fiber.cp2x + fiber.cp2ox;
  const cp2y = fiber.cp2y + fiber.cp2oy;
  const headPos = cubicBezier(fiber.p0x, fiber.p0y, cp1x, cp1y, cp2x, cp2y, fiber.p3x, fiber.p3y, pulse.t);
  ctx.beginPath();
  ctx.arc(headPos.x, headPos.y, pulse.size * 1.2, 0, Math.PI * 2);
  ctx.fillStyle = `hsla(${pulse.hue},100%,80%,${pulse.opacity * 0.8})`;
  ctx.fill();
}
// ── Main Component ─────────────────────────────────────────────────────────
export function FractalBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const mobile = window.innerWidth < 768;
    const ctx = canvas.getContext('2d', {
      alpha: false
    });
    if (!ctx) return;
    // GPU compositing hint
    canvas.style.transform = 'translateZ(0)';
    canvas.style.willChange = 'transform';
    let W = window.innerWidth;
    let H = window.innerHeight;
    // ── Cached gradients (recreated only on resize) ──────────────────────
    let cachedBg: CanvasGradient;
    let cachedVig: CanvasGradient;
    let cachedCenterDark: CanvasGradient;
    function buildCachedGradients() {
      // Background radial gradient
      cachedBg = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, Math.max(W, H) * 0.8);
      cachedBg.addColorStop(0, 'rgba(3,5,14,1)');
      cachedBg.addColorStop(0.3, 'rgba(2,4,12,1)');
      cachedBg.addColorStop(0.7, 'rgba(1,3,10,1)');
      cachedBg.addColorStop(1, 'rgba(1,2,8,1)');
      // Vignette
      cachedVig = ctx.createRadialGradient(W / 2, H / 2, H * 0.1, W / 2, H / 2, Math.max(W, H) * 0.82);
      cachedVig.addColorStop(0, 'rgba(0,0,0,0)');
      cachedVig.addColorStop(0.6, 'rgba(0,0,0,0.1)');
      cachedVig.addColorStop(1, 'rgba(0,0,0,0.88)');
      // Center darkness
      cachedCenterDark = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, Math.min(W, H) * 0.38);
      cachedCenterDark.addColorStop(0, 'rgba(1,3,10,0.55)');
      cachedCenterDark.addColorStop(0.5, 'rgba(1,3,10,0.2)');
      cachedCenterDark.addColorStop(1, 'rgba(0,0,0,0)');
    }
    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W;
      canvas.height = H;
      buildCachedGradients();
    };
    resize();
    window.addEventListener('resize', resize);
    // Build fibers — drastically fewer on mobile
    const fibers: Fiber[] = [];
    const fiberCounts = mobile ? [5, 3, 2] : [Math.floor(W * H / 18000) + 8, Math.floor(W * H / 22000) + 5, Math.floor(W * H / 30000) + 3];
    for (let layer = 0; layer < 3; layer++) {
      for (let i = 0; i < fiberCounts[layer]; i++) {
        fibers.push(buildFiber(W, H, layer));
      }
    }
    fibers.sort((a, b) => a.layer - b.layer);
    // On mobile: boost pulse speeds so animation feels alive
    if (mobile) {
      for (const fiber of fibers) {
        for (const pulse of fiber.pulses) {
          pulse.speed *= 2.5;
        }
      }
    }
    let lastTime = 0;
    const draw = (timestamp: number) => {
      const dt = Math.min(timestamp - lastTime, 50);
      lastTime = timestamp;
      // ── Background ──
      if (mobile) {
        // Solid fill — no gradient cost
        ctx.fillStyle = '#03050e';
        ctx.fillRect(0, 0, W, H);
      } else {
        ctx.fillStyle = cachedBg;
        ctx.fillRect(0, 0, W, H);
      }
      // ── Fog layers ──
      const t = timestamp * 0.00018;
      const fogConfigs = mobile ? [{
        fx: 0.1,
        fy: 0.15,
        r: 0.32,
        color: 'rgba(10,30,120,',
        base: 0.025
      }, {
        fx: 0.9,
        fy: 0.78,
        r: 0.24,
        color: 'rgba(30,10,110,',
        base: 0.016
      }] : [{
        fx: 0.1,
        fy: 0.15,
        r: 0.32,
        color: 'rgba(10,30,120,',
        base: 0.025
      }, {
        fx: 0.88,
        fy: 0.12,
        r: 0.26,
        color: 'rgba(50,15,130,',
        base: 0.02
      }, {
        fx: 0.05,
        fy: 0.72,
        r: 0.28,
        color: 'rgba(5,50,140,',
        base: 0.018
      }, {
        fx: 0.9,
        fy: 0.78,
        r: 0.24,
        color: 'rgba(30,10,110,',
        base: 0.016
      }, {
        fx: 0.5,
        fy: 0.05,
        r: 0.2,
        color: 'rgba(0,60,150,',
        base: 0.014
      }];
      for (let i = 0; i < fogConfigs.length; i++) {
        const f = fogConfigs[i];
        const nx = W * f.fx + Math.sin(t + i * 1.4) * W * 0.025;
        const ny = H * f.fy + Math.cos(t * 0.75 + i * 1.1) * H * 0.025;
        const r = Math.min(W, H) * f.r;
        const fog = ctx.createRadialGradient(nx, ny, 0, nx, ny, r);
        const alpha = f.base + Math.sin(t * 1.1 + i * 0.8) * 0.005;
        fog.addColorStop(0, `${f.color}${alpha})`);
        fog.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = fog;
        ctx.fillRect(0, 0, W, H);
      }
      // ── Edge bleed — desktop only ──
      if (!mobile) {
        const bleedA = 0.03 + Math.sin(timestamp * 0.00012) * 0.008;
        const bleedZ = Math.min(W, H) * 0.18;
        [{
          x0: 0,
          y0: 0,
          x1: 0,
          y1: bleedZ,
          rx: 0,
          ry: 0,
          rw: W,
          rh: bleedZ
        }, {
          x0: W,
          y0: 0,
          x1: W - bleedZ,
          y1: 0,
          rx: W - bleedZ,
          ry: 0,
          rw: bleedZ,
          rh: H
        }, {
          x0: 0,
          y0: H,
          x1: 0,
          y1: H - bleedZ,
          rx: 0,
          ry: H - bleedZ,
          rw: W,
          rh: bleedZ
        }, {
          x0: 0,
          y0: 0,
          x1: bleedZ,
          y1: 0,
          rx: 0,
          ry: 0,
          rw: bleedZ,
          rh: H
        }].forEach((cfg, i) => {
          const hue = 210 + i * 15;
          const g = ctx.createLinearGradient(cfg.x0, cfg.y0, cfg.x1, cfg.y1);
          g.addColorStop(0, `hsla(${hue},80%,55%,${bleedA})`);
          g.addColorStop(0.5, `hsla(${hue + 20},70%,45%,${bleedA * 0.3})`);
          g.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = g;
          ctx.fillRect(cfg.rx, cfg.ry, cfg.rw, cfg.rh);
        });
      }
      // ── Draw fibers ──
      for (const fiber of fibers) {
        fiber.cp1ox += fiber.cp1vx * dt;
        fiber.cp1oy += fiber.cp1vy * dt;
        fiber.cp2ox += fiber.cp2vx * dt;
        fiber.cp2oy += fiber.cp2vy * dt;
        const maxOffset = 30 + fiber.layer * 15;
        if (Math.abs(fiber.cp1ox) > maxOffset) fiber.cp1vx *= -1;
        if (Math.abs(fiber.cp1oy) > maxOffset) fiber.cp1vy *= -1;
        if (Math.abs(fiber.cp2ox) > maxOffset) fiber.cp2vx *= -1;
        if (Math.abs(fiber.cp2oy) > maxOffset) fiber.cp2vy *= -1;
        if (mobile) {
          drawFiberSimple(ctx, fiber, timestamp);
        } else {
          drawFiber(ctx, fiber, timestamp, W, H);
        }
        for (const pulse of fiber.pulses) {
          if (mobile) {
            drawPulseSimple(ctx, fiber, pulse);
          } else {
            drawPulse(ctx, fiber, pulse);
          }
        }
        for (const pulse of fiber.pulses) {
          pulse.t += pulse.speed * dt;
          pulse.hue += 0.008 * dt;
          if (pulse.hue > 280) pulse.hue -= 80;
        }
        fiber.pulses = fiber.pulses.filter(p => p.t < 1.05);
        const maxPulses = mobile ? 1 : fiber.layer === 2 ? 3 : fiber.layer === 1 ? 2 : 1;
        if (fiber.pulses.length < maxPulses && Math.random() < 0.0008 * (fiber.layer + 1)) {
          const lp = [0.6, 1.0, 1.5][fiber.layer];
          const speed = (0.0003 + Math.random() * 0.0005) * lp * (mobile ? 2.5 : 1);
          fiber.pulses.push({
            t: 0,
            speed,
            opacity: 0.45 + Math.random() * 0.55,
            hue: fiber.hue + (Math.random() - 0.5) * 40,
            size: (1.5 + Math.random() * 2) * (fiber.layer + 1),
            trail: 0.05 + Math.random() * 0.07
          });
        }
      }
      // ── Central darkness + vignette ──
      if (!mobile) {
        ctx.fillStyle = cachedVig;
        ctx.fillRect(0, 0, W, H);
      } else {
        ctx.fillStyle = cachedVig;
        ctx.fillRect(0, 0, W, H);
      }
      animRef.current = requestAnimationFrame(draw);
    };
    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none" style={{
    zIndex: 0
  }} aria-hidden="true" data-id="element-69" />;
}